/**
 * Loop expansion — converts shared-vertex + per-loop-normal data into
 * per-corner arrays suitable for GPU buffer attributes.
 *
 * In the shared-vertex model, `vertices` has one position per unique spatial
 * point (8 for a cube) and `loopNormals` has one normal per face corner
 * (24 for a cube). The GPU needs per-corner positions too (since adjacent
 * faces may have different normals at the same vertex). This module
 * expands shared positions into per-corner arrays that the render-space
 * triangle indices (from `triangulateTopologyForRendering`) can index into.
 */

import {
  asSharedTriangleIndices,
  type RenderTriangleIndices,
  type SharedTriangleIndices,
} from "./triangleIndexTypes.ts";
import type { LoopNormals, PolygonTopology } from "./types.ts";

export interface ExpandedMesh {
  /** Per-corner positions (length = corners.length × 3). */
  renderVertices: Float32Array;
  /** Per-corner normals — same as loopNormals (length = corners.length × 3). */
  renderNormals: Float32Array;
  /** Per-corner UVs if provided (length = corners.length × 2). */
  renderUVs?: Float32Array;
}

/**
 * Expand shared vertex positions into per-corner arrays for GPU rendering.
 *
 * For each corner c in the topology:
 *   renderVertices[c*3..+3] = vertices[corners[c]*3..+3]
 *   renderNormals[c*3..+3]  = loopNormals[c*3..+3]  (already per-corner)
 *   renderUVs[c*2..+2]      = uvs[c*2..+2]           (already per-corner)
 */
export function expandForRendering(
  vertices: Float32Array,
  loopNormals: LoopNormals,
  topology: PolygonTopology,
  uvs?: Float32Array,
): ExpandedMesh {
  const { corners } = topology;
  const cornerCount = corners.length;

  if (import.meta.env.DEV) {
    const vertexCount = vertices.length / 3;
    for (let c = 0; c < cornerCount; c++) {
      if (corners[c] >= vertexCount) {
        throw new Error(
          `expandForRendering: corner[${c}] = ${corners[c]} exceeds vertex count ${vertexCount}`,
        );
      }
    }
    if (loopNormals.length !== cornerCount * 3) {
      throw new Error(
        `expandForRendering: loopNormals length ${loopNormals.length} !== cornerCount*3 (${cornerCount * 3})`,
      );
    }
    if (uvs && uvs.length !== cornerCount * 2) {
      throw new Error(
        `expandForRendering: uvs length ${uvs.length} !== cornerCount*2 (${cornerCount * 2})`,
      );
    }
  }

  const renderVertices = new Float32Array(cornerCount * 3);
  for (let c = 0; c < cornerCount; c++) {
    const vi = corners[c];
    renderVertices[c * 3] = vertices[vi * 3];
    renderVertices[c * 3 + 1] = vertices[vi * 3 + 1];
    renderVertices[c * 3 + 2] = vertices[vi * 3 + 2];
  }

  // loopNormals is already per-corner — copy directly for GPU ownership
  const renderNormals = new Float32Array(loopNormals);

  const result: ExpandedMesh = { renderVertices, renderNormals };

  if (uvs && uvs.length === cornerCount * 2) {
    result.renderUVs = new Float32Array(uvs);
  }

  return result;
}

/**
 * Convert corner-based render indices to shared-vertex indices.
 *
 * Modifiers (subsurf, mirror) need shared-vertex topology so adjacent
 * faces share edge vertices. The WASM mesh extractor outputs corner-based
 * render indices (0..cornerCount-1). This maps each render index to the
 * shared vertex index via topology.corners.
 */
export function buildSharedVertexIndices(
  renderIndices: RenderTriangleIndices,
  topology: PolygonTopology,
): SharedTriangleIndices {
  const { corners } = topology;

  if (import.meta.env.DEV) {
    for (let i = 0; i < renderIndices.length; i++) {
      if (renderIndices[i] >= corners.length) {
        throw new Error(
          `buildSharedVertexIndices: renderIndices[${i}] = ${renderIndices[i]} >= cornerCount ${corners.length}`,
        );
      }
    }
  }

  const shared = new Uint32Array(renderIndices.length);
  for (let i = 0; i < renderIndices.length; i++) {
    shared[i] = corners[renderIndices[i]];
  }
  return asSharedTriangleIndices(shared);
}

/**
 * Collapse per-corner render positions back to shared-vertex positions.
 *
 * Rendering expands shared vertices to one position per face corner so the GPU
 * can pair them with per-corner normals/UVs. Sketch caches, however, operate in
 * the shared-vertex space. When a live mesh hands us corner-expanded positions
 * (for example from a rendered/deformed BufferGeometry), map the first corner
 * for each shared vertex back onto that vertex index. Any fallback positions are
 * preserved for shared vertices that are not referenced by a face corner.
 */
export function collapseRenderVerticesToSharedPositions(
  renderVertices: Float32Array,
  topology: PolygonTopology,
  fallbackSharedVertices?: Float32Array,
): Float32Array {
  const { corners } = topology;
  const vertexCount = fallbackSharedVertices
    ? fallbackSharedVertices.length / 3
    : corners.reduce((max, vi) => Math.max(max, vi), -1) + 1;

  if (import.meta.env.DEV && renderVertices.length !== corners.length * 3) {
    throw new Error(
      `collapseRenderVerticesToSharedPositions: renderVertices length ${renderVertices.length} ` +
        `!== cornerCount*3 (${corners.length * 3})`,
    );
  }

  const shared = fallbackSharedVertices
    ? new Float32Array(fallbackSharedVertices)
    : new Float32Array(vertexCount * 3);
  const assigned = new Uint8Array(vertexCount);

  for (let c = 0; c < corners.length; c++) {
    const vi = corners[c];
    if (assigned[vi]) continue;
    shared[vi * 3] = renderVertices[c * 3];
    shared[vi * 3 + 1] = renderVertices[c * 3 + 1];
    shared[vi * 3 + 2] = renderVertices[c * 3 + 2];
    assigned[vi] = 1;
  }

  return shared;
}

/**
 * Expand per-vertex morph deltas to per-corner arrays.
 *
 * Shape key deltas are stored per shared vertex (vertexCount entries).
 * After expandForRendering, the position buffer has cornerCount entries.
 * Morph attributes must match the position count, so deltas need the
 * same expansion: delta[corner] = delta[corners[corner]].
 */
export function expandMorphDeltas(
  deltas: Float32Array,
  topology: PolygonTopology,
): Float32Array {
  const { corners } = topology;
  const cornerCount = corners.length;

  if (import.meta.env.DEV) {
    const vertexCount = deltas.length / 3;
    for (let c = 0; c < cornerCount; c++) {
      if (corners[c] >= vertexCount) {
        throw new Error(
          `expandMorphDeltas: corner[${c}] = ${corners[c]} >= vertexCount ${vertexCount}`,
        );
      }
    }
  }

  const expanded = new Float32Array(cornerCount * 3);
  for (let c = 0; c < cornerCount; c++) {
    const vi = corners[c];
    expanded[c * 3] = deltas[vi * 3];
    expanded[c * 3 + 1] = deltas[vi * 3 + 1];
    expanded[c * 3 + 2] = deltas[vi * 3 + 2];
  }
  return expanded;
}

/**
 * Convert per-loop UVs to per-vertex UVs for shared-vertex geometry.
 *
 * At UV seams a vertex has different UVs for different faces. In shared-
 * vertex mode we can only store one UV per vertex, so the first loop
 * referencing each vertex wins. This is acceptable for modifier input
 * where subdivision will interpolate UVs anyway.
 */
export function perVertexUVsFromLoopUVs(
  loopUVs: Float32Array,
  topology: PolygonTopology,
  vertexCount: number,
): Float32Array {
  const { corners } = topology;
  const uvs = new Float32Array(vertexCount * 2);
  const assigned = new Uint8Array(vertexCount);
  for (let c = 0; c < corners.length; c++) {
    const vi = corners[c];
    if (!assigned[vi]) {
      uvs[vi * 2] = loopUVs[c * 2];
      uvs[vi * 2 + 1] = loopUVs[c * 2 + 1];
      assigned[vi] = 1;
    }
  }
  return uvs;
}
